import type { SupabaseClient } from "@supabase/supabase-js";
import { accessRoleLabel } from "@/lib/access-permissions";
import { LoadingStatus } from "./activity";
import { lazy, Suspense, useMemo, useState } from "react";
import { Check, Clock3, LoaderCircle, RotateCcw, ShieldCheck, ShieldX, UserMinus, UserRoundCog, X } from "lucide-react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import type { Language } from "@/lib/finance";
import type { AccessProfile } from "@/lib/supabase-repository";

type ReviewDecision = "approve" | "reject" | "restore";
const RolePolicyEditor = lazy(() => import("./role-policy-editor").then(module => ({ default: module.RolePolicyEditor })));

type Props = {
  client: SupabaseClient;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  profiles: AccessProfile[];
  currentProfileId?: string;
  language: Language;
  onChangeAccess: (profileId: string, access: AccessProfile["access"]) => Promise<void>;
  onReviewAccess: (profileId: string, decision: ReviewDecision) => Promise<void>;
  onRemoveAccess: (profileId: string) => Promise<void>;
};

export function AccessSettings({ client, open, onOpenChange, profiles, currentProfileId, language, onChangeAccess, onReviewAccess, onRemoveAccess }: Props) {
  const [busyId, setBusyId] = useState("");
  const [confirmation, setConfirmation] = useState<{ id: string; action: "reject" | "remove" } | null>(null);
  const activeProfiles = useMemo(() => profiles.filter((item) => item.status === "active"), [profiles]);
  const pendingProfiles = useMemo(() => profiles.filter((item) => item.status === "pending"), [profiles]);
  const removedProfiles = useMemo(() => profiles.filter((item) => item.status === "removed"), [profiles]);
  const owners = useMemo(() => activeProfiles.filter((item) => item.access === "owner").length, [activeProfiles]);
  const text = language === "id" ? {
    title: "Akses pengguna",
    description: "Setujui akun baru, atur peran, atau cabut akses tanpa menghapus histori.",
    owner: "Owner",
    user: "User",
    current: "Akun Anda",
    pending: "Menunggu persetujuan",
    active: "Pengguna aktif",
    removed: "Akses dicabut",
    noPending: "Tidak ada akun yang menunggu persetujuan.",
    ownerHelp: "Impor, edit, pengaturan, budgeting, dan pengelolaan akses.",
    userHelp: "Dashboard, detail transaksi, filter, laporan, dan ekspor.",
    approve: "Setujui sebagai User",
    reject: "Tolak",
    remove: "Cabut akses",
    restore: "Pulihkan sebagai User",
    cancel: "Batal",
    rejectConfirm: "Tolak pendaftaran ini? Akun tidak akan dapat membaca data, tetapi catatan profil tetap tersimpan.",
    removeConfirm: "Cabut akses pengguna ini? Histori tetap tersimpan dan akses dapat dipulihkan kembali.",
    saved: "Hak akses berhasil diperbarui.",
    failed: "Perubahan akses gagal. Periksa pengaturan akun dan migrasi v0.19.9; minimal satu Owner harus tetap aktif.",
  } : {
    title: "User access",
    description: "Approve new accounts, manage roles, or revoke access without deleting history.",
    owner: "Owner",
    user: "User",
    current: "Your account",
    pending: "Awaiting approval",
    active: "Active users",
    removed: "Revoked access",
    noPending: "No accounts are awaiting approval.",
    ownerHelp: "Import, editing, settings, budgeting, and access management.",
    userHelp: "Dashboard, transaction details, filters, reports, and exports.",
    approve: "Approve as User",
    reject: "Reject",
    remove: "Revoke access",
    restore: "Restore as User",
    cancel: "Cancel",
    rejectConfirm: "Reject this registration? The account cannot read data, while its profile record is preserved.",
    removeConfirm: "Revoke this user's access? History is preserved and access can be restored later.",
    saved: "Access was updated.",
    failed: "Access could not be updated. Check account settings and the v0.19.9 migration; at least one Owner must remain active.",
  };

  async function run(profileId: string, action: () => Promise<void>) {
    setBusyId(profileId);
    try {
      await action();
      setConfirmation(null);
      toast.success(text.saved);
    } catch {
      toast.error(text.failed);
    } finally {
      setBusyId("");
    }
  }

  async function change(profile: AccessProfile, access: AccessProfile["access"]) {
    if (profile.access === access) return;
    await run(profile.id, () => onChangeAccess(profile.id, access));
  }

  async function review(profile: AccessProfile, decision: ReviewDecision) {
    await run(profile.id, () => onReviewAccess(profile.id, decision));
  }

  async function remove(profile: AccessProfile) {
    await run(profile.id, () => onRemoveAccess(profile.id));
  }

  function Identity({ profile }: { profile: AccessProfile }) {
    return <div className="min-w-0"><div className="flex flex-wrap items-center gap-2"><p className="truncate font-semibold">{profile.displayName}</p>{profile.id === currentProfileId && <Badge variant="secondary">{text.current}</Badge>}</div>{profile.email && <p className="mt-1 truncate text-xs text-muted-foreground">{profile.email}</p>}</div>;
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-2xl">
        <SheetHeader className="border-b px-4 py-5 sm:px-6">
          <SheetTitle className="flex items-center gap-2"><ShieldCheck className="size-5" />{text.title}{pendingProfiles.length > 0 && <Badge className="rounded-full">{pendingProfiles.length}</Badge>}</SheetTitle>
          <SheetDescription>{text.description}</SheetDescription>
        </SheetHeader>
        <div className="space-y-7 p-4 sm:p-6">
          {open && <Suspense fallback={<LoadingStatus label={language === "id" ? "Memuat batas akses…" : "Loading permissions…"}/>}><RolePolicyEditor client={client} language={language} /></Suspense>}
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="rounded-2xl border bg-fuchsia-500/7 p-4"><p className="font-semibold text-fuchsia-700 dark:text-fuchsia-300">{text.owner}</p><p className="mt-2 text-sm leading-6 text-muted-foreground">{text.ownerHelp}</p></div>
            <div className="rounded-2xl border bg-cyan-500/7 p-4"><p className="font-semibold text-cyan-700 dark:text-cyan-300">{text.user}</p><p className="mt-2 text-sm leading-6 text-muted-foreground">{text.userHelp}</p></div>
          </div>

          <section>
            <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold"><Clock3 className="size-4 text-amber-500" />{text.pending}<Badge variant="outline">{pendingProfiles.length}</Badge></h3>
            <div className="space-y-3">
              {!pendingProfiles.length && <p className="rounded-2xl border border-dashed p-4 text-sm text-muted-foreground">{text.noPending}</p>}
              {pendingProfiles.map((profile) => <article key={profile.id} className="rounded-2xl border border-amber-500/25 bg-amber-500/5 p-4">
                <Identity profile={profile} />
                {confirmation?.id === profile.id && confirmation.action === "reject" ? <div className="mt-4 rounded-xl border bg-background p-3"><p className="text-sm leading-6">{text.rejectConfirm}</p><div className="mt-3 flex flex-wrap justify-end gap-2"><Button variant="outline" size="sm" onClick={() => setConfirmation(null)}>{text.cancel}</Button><Button variant="destructive" size="sm" disabled={busyId === profile.id} onClick={() => void review(profile, "reject")}>{busyId === profile.id ? <LoaderCircle className="animate-spin" /> : <X />}{text.reject}</Button></div></div> : <div className="mt-4 grid gap-2 sm:grid-cols-2"><Button disabled={busyId === profile.id} onClick={() => void review(profile, "approve")}><Check />{text.approve}</Button><Button variant="outline" disabled={busyId === profile.id} onClick={() => setConfirmation({ id: profile.id, action: "reject" })}><ShieldX />{text.reject}</Button></div>}
              </article>)}
            </div>
          </section>

          <section>
            <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold"><UserRoundCog className="size-4 text-fuchsia-500" />{text.active}<Badge variant="outline">{activeProfiles.length}</Badge></h3>
            <div className="space-y-3">
              {activeProfiles.map((profile) => {
                const isLastOwner = profile.access === "owner" && owners <= 1;
                const canRemove = profile.access !== "owner" && profile.id !== currentProfileId;
                return <article key={profile.id} className="rounded-2xl border p-4">
                  <div className="grid gap-3 sm:grid-cols-[1fr_170px] sm:items-center">
                    <div><Identity profile={profile} /><p className="mt-2 text-xs text-muted-foreground">{profile.access === "owner" ? text.ownerHelp : profile.access === "customer_care" ? (language === "id" ? "Bawaan: hanya mutasi Kredit." : "Default: credit transactions only.") : profile.access === "leader" ? (language === "id" ? "Bawaan: Debet/Kredit dan ringkasan serta grafik." : "Default: debit/credit, summary and charts.") : text.userHelp}</p></div>
                    <Select value={profile.access} disabled={busyId === profile.id} onValueChange={(value) => void change(profile, value as AccessProfile["access"])}>
                      <SelectTrigger className="w-full">{busyId === profile.id ? <LoaderCircle className="animate-spin" /> : <UserRoundCog />}<SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="owner">{text.owner}</SelectItem><SelectItem value="user" disabled={isLastOwner}>{text.user}</SelectItem><SelectItem value="customer_care" disabled={isLastOwner}>Customer Care</SelectItem><SelectItem value="leader" disabled={isLastOwner}>Leader</SelectItem></SelectContent>
                    </Select>
                  </div>
                  {confirmation?.id === profile.id && confirmation.action === "remove" ? <div className="mt-4 rounded-xl border border-red-500/20 bg-red-500/5 p-3"><p className="text-sm leading-6">{text.removeConfirm}</p><div className="mt-3 flex flex-wrap justify-end gap-2"><Button variant="outline" size="sm" onClick={() => setConfirmation(null)}>{text.cancel}</Button><Button variant="destructive" size="sm" disabled={busyId === profile.id} onClick={() => void remove(profile)}>{busyId === profile.id ? <LoaderCircle className="animate-spin" /> : <UserMinus />}{text.remove}</Button></div></div> : canRemove && <div className="mt-3 flex justify-end"><Button variant="ghost" size="sm" className="text-destructive hover:text-destructive" onClick={() => setConfirmation({ id: profile.id, action: "remove" })}><UserMinus />{text.remove}</Button></div>}
                </article>;
              })}
            </div>
          </section>

          {removedProfiles.length > 0 && <section>
            <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold"><ShieldX className="size-4 text-muted-foreground" />{text.removed}<Badge variant="outline">{removedProfiles.length}</Badge></h3>
            <div className="space-y-3">{removedProfiles.map((profile) => <article key={profile.id} className="flex flex-col gap-3 rounded-2xl border bg-muted/20 p-4 sm:flex-row sm:items-center sm:justify-between"><Identity profile={profile} /><Button variant="outline" disabled={busyId === profile.id} onClick={() => void review(profile, "restore")}>{busyId === profile.id ? <LoaderCircle className="animate-spin" /> : <RotateCcw />}{text.restore}</Button></article>)}</div>
          </section>}
        </div>
      </SheetContent>
    </Sheet>
  );
}
